"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import {
  BookOpen,
  ChevronRight,
  Menu,
  X,
  Hash,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { LegalFooter } from "@/components/LegalFooter";

// ─── Types ────────────────────────────────────────────────────────────────────

export type GuideItem = {
  id: string;
  label: string;
};

export type GuideSection = {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: GuideItem[];
};

// ─── Primitive content components ────────────────────────────────────────────

export function SectionHeading({
  id,
  children,
  level = 1,
}: {
  id: string;
  children: React.ReactNode;
  level?: 1 | 2;
}) {
  return level === 1 ? (
    <h2
      id={id}
      className="scroll-mt-28 text-2xl font-semibold tracking-tight text-foreground flex items-center gap-2 group"
    >
      <a href={`#${id}`} className="opacity-0 group-hover:opacity-40 transition-opacity">
        <Hash className="h-5 w-5" />
      </a>
      {children}
    </h2>
  ) : (
    <h3
      id={id}
      className="scroll-mt-28 text-lg font-semibold tracking-tight text-foreground flex items-center gap-2 group mt-8"
    >
      <a href={`#${id}`} className="opacity-0 group-hover:opacity-40 transition-opacity">
        <Hash className="h-4 w-4" />
      </a>
      {children}
    </h3>
  );
}

export function Callout({
  type = "info",
  children,
}: {
  type?: "info" | "tip" | "warn";
  children: React.ReactNode;
}) {
  const styles = {
    info: "border-[hsl(var(--info)/0.25)] bg-[hsl(var(--info)/0.08)] text-info",
    tip: "border-[hsl(var(--success)/0.25)] bg-[hsl(var(--success)/0.08)] text-success",
    warn: "border-[hsl(var(--warning)/0.25)] bg-[hsl(var(--warning)/0.08)] text-warning",
  };
  const icons = {
    info: <Info className="h-4 w-4 shrink-0 mt-0.5" />,
    tip: <ChevronRight className="h-4 w-4 shrink-0 mt-0.5" />,
    warn: <Info className="h-4 w-4 shrink-0 mt-0.5" />,
  };
  return (
    <div className={`my-4 flex gap-3 rounded-xl border px-4 py-3 text-sm leading-6 ${styles[type]}`}>
      {icons[type]}
      <div>{children}</div>
    </div>
  );
}

export function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="my-4 overflow-x-auto rounded-xl border border-border/60 bg-muted/60 px-4 py-3 text-xs leading-6 text-foreground/90 font-mono">
      {children}
    </pre>
  );
}

export function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-lg border border-border px-2 py-0.5 font-mono text-xs font-semibold transition-colors">
      {children}
    </span>
  );
}

export function FileFormatRow({ ext, name, desc }: { ext: string; name: string; desc: string }) {
  return (
    <tr className="border-b border-border/40">
      <td className="py-2.5 pr-4">
        <Tag>{ext}</Tag>
      </td>
      <td className="py-2.5 pr-4 text-sm font-medium text-foreground">{name}</td>
      <td className="py-2.5 text-sm text-muted-foreground">{desc}</td>
    </tr>
  );
}

export function FaqList({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <details
          key={i}
          className="group rounded-xl border border-border/60 bg-card px-4 py-3 transition-colors open:border-primary/30"
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
            <span className="leading-6">{item.q}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.a}</p>
        </details>
      ))}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({
  sections,
  activeId,
  onSelect,
  onClose,
  linkPrefix,
  sectionHref,
}: {
  sections: GuideSection[];
  activeId: string;
  onSelect?: (id: string) => void;
  onClose?: () => void;
  linkPrefix?: string;
  sectionHref?: string;
}) {
  function hrefFor(id: string) {
    return linkPrefix ? `${linkPrefix}${id}` : `#${id}`;
  }

  return (
    <nav className="space-y-1">
      {sections.map((section) => (
        <div key={section.id} className="space-y-0.5">
          <a
            href={sectionHref ?? hrefFor(section.id)}
            onClick={() => { onSelect?.(section.id); onClose?.(); }}
            data-nav-id={section.id}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              activeId === section.id
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            }`}
          >
            <span className="text-primary/80">{section.icon}</span>
            {section.label}
          </a>
          <div className="ml-7 space-y-0.5">
            {section.items.map((item) => (
              <a
                key={item.id}
                href={hrefFor(item.id)}
                onClick={() => { onSelect?.(item.id); onClose?.(); }}
                data-nav-id={item.id}
                className={`block rounded-lg px-3 py-1.5 text-xs transition-colors ${
                  activeId === item.id
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

// ─── Page layout ─────────────────────────────────────────────────────────────

export function GuidesPageLayout({
  sections,
  title,
  subtitle,
  breadcrumb,
  backHref = "/guides",
  linkPrefix,
  activeId: activeIdProp,
  children,
}: {
  sections: GuideSection[];
  title: string;
  subtitle: string;
  breadcrumb: string;
  backHref?: string;
  /**
   * If set, sidebar links use `${linkPrefix}${id}` instead of in-page anchors.
   */
  linkPrefix?: string;
  /**
   * If set, sidebar highlights this id and scroll-tracking is disabled.
   */
  activeId?: string;
  /**
   * Kept for back-compat in callsites. No longer used inside the layout —
   * each guide section now embeds its own <SectionForum> directly.
   */
  guideId?: string;
  children: React.ReactNode;
}) {
  const [activeId, setActiveId] = useState(activeIdProp ?? sections[0]?.id ?? "");
  const [mobileOpen, setMobileOpen] = useState(false);

  const allIds = sections.flatMap((s) => [s.id, ...s.items.map((i) => i.id)]);
  const sidebarAsideRef = useRef<HTMLElement | null>(null);

  // When activeIdProp is provided, the page is showing a single section and
  // there is no scroll-tracking to do — just keep the highlight pinned.
  useEffect(() => {
    if (activeIdProp) {
      setActiveId(activeIdProp);
      return;
    }
    const OFFSET = 120;
    const update = () => {
      let current = allIds[0];
      for (const id of allIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= OFFSET) {
          current = id;
        }
      }
      setActiveId(current);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdProp]);

  // Scroll only the sidebar <aside> — never touches the page scroll position
  useEffect(() => {
    const aside = sidebarAsideRef.current;
    if (!aside) return;
    const link = aside.querySelector<HTMLElement>(`[data-nav-id="${activeId}"]`);
    if (!link) return;
    // Position of the link relative to the aside's scrollable content
    const linkTop = link.getBoundingClientRect().top - aside.getBoundingClientRect().top + aside.scrollTop;
    const linkBottom = linkTop + link.offsetHeight;
    const asideHeight = aside.clientHeight;
    // Only move if the link is outside the currently visible strip
    if (linkTop < aside.scrollTop || linkBottom > aside.scrollTop + asideHeight) {
      aside.scrollTop = linkTop - asideHeight / 2 + link.offsetHeight / 2;
    }
  }, [activeId]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/92 backdrop-blur">
        <div className="mx-auto flex max-w-[90rem] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full lg:hidden"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="sm" asChild className="rounded-full text-muted-foreground hover:text-foreground">
              <Link href={backHref}>
                <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
                Back
              </Link>
            </Button>
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground">
              <Link href="/guides" className="hover:text-foreground transition-colors flex items-center gap-1.5">
                <BookOpen className="h-4 w-4" />
                <span>Guides</span>
              </Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="font-medium text-foreground">{breadcrumb}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="hidden sm:inline-flex rounded-full text-xs">
              Workshop Docs
            </Badge>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
          <div
            className="absolute left-0 top-[57px] bottom-0 w-72 overflow-y-auto border-r border-border/60 bg-background p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Sidebar sections={sections} activeId={activeId} onSelect={setActiveId} onClose={() => setMobileOpen(false)} linkPrefix={linkPrefix} sectionHref={backHref} />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="mx-auto flex max-w-[90rem]">
        {/* Desktop sidebar */}
        <aside
          ref={sidebarAsideRef as React.RefObject<HTMLElement>}
          className="hidden lg:block sticky top-[57px] h-[calc(100vh-57px)] w-64 shrink-0 overflow-y-auto border-r border-border/50 p-5"
        >
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Contents
          </p>
          <Sidebar sections={sections} activeId={activeId} onSelect={setActiveId} linkPrefix={linkPrefix} sectionHref={backHref} />
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 px-4 pt-10 pb-6 sm:px-8 lg:px-12 xl:max-w-4xl">
          <div className="mb-10">
            <p className="text-xs uppercase tracking-[0.28em] text-primary/80 mb-3">Documentation</p>
            <h1 className="text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">{title}</h1>
            <p className="mt-4 text-base leading-7 text-muted-foreground max-w-2xl">{subtitle}</p>
          </div>

          {children}
        </main>

        <div className="hidden xl:block w-8 shrink-0" />
      </div>

      <LegalFooter />
    </div>
  );
}
